/**
 * Mapbox Geocoding API Integration
 *
 * Replaces Nominatim with Mapbox Geocoding v6 API for more accurate
 * address and POI search results.
 *
 * Uses existing VITE_MAPBOX_PUBLIC_TOKEN configuration.
 */

import { env } from '@/config/environment';
import { calculateDistance } from './routeOptimization';

// =============================================================================
// Types (compatible with existing GeocodedResult interface)
// =============================================================================

export type AddressType = 'street_address' | 'street' | 'city' | 'region' | 'other';

export interface GeocodedResult {
  lat: number;
  lng: number;
  displayName: string;
  businessName?: string;
  type: AddressType;
  importance: number;
  addressComponents: {
    houseNumber?: string;
    road?: string;
    city?: string;
    state?: string;
    postcode?: string;
  };
  phone?: string;
  warning?: string;
}

interface MapboxGeocodeOptions {
  limit?: number;
  /**
   * Filter results by feature type.
   * Valid Geocoding v6 types: address, place, locality, neighborhood, street,
   * postcode, district, region, country, block, secondary_address.
   * Note: 'poi' is NOT supported - use Mapbox Search Box API for POI search.
   */
  types?: string[];
  proximity?: { lat: number; lng: number };
}

// =============================================================================
// Mapbox API Response Types
// =============================================================================

interface MapboxContext {
  place?: { name: string };
  region?: { name: string; region_code?: string };
  postcode?: { name: string };
  street?: { name: string };
  address?: { name: string; address_number?: string };
  country?: { name: string };
}

interface MapboxFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  properties: {
    mapbox_id: string;
    feature_type: string;
    name: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    context: MapboxContext;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
}

interface MapboxGeocodeResponse {
  type: 'FeatureCollection';
  features: MapboxFeature[];
  attribution: string;
}

// =============================================================================
// Mapbox Feature Type to AddressType Mapping
// =============================================================================

const FEATURE_TYPE_MAP: Record<string, AddressType> = {
  address: 'street_address',
  poi: 'street_address',
  street: 'street',
  place: 'city',
  locality: 'city',
  neighborhood: 'city',
  region: 'region',
  district: 'region',
  country: 'other',
  postcode: 'other'
};

function mapFeatureType(featureType: string): AddressType {
  return FEATURE_TYPE_MAP[featureType] || 'other';
}

// =============================================================================
// Warning Messages
// =============================================================================

const ADDRESS_WARNINGS: Record<AddressType, string | undefined> = {
  city: 'This is a city. Enter a street address for more accurate routing.',
  region: 'This is a region. Enter a specific street address.',
  street: 'No house number found. Location may be approximate.',
  street_address: undefined,
  other: undefined
};

// =============================================================================
// Result Deduplication
// =============================================================================

const DUPLICATE_THRESHOLD_MILES = 0.05; // ~80 meters

function deduplicateResults(results: GeocodedResult[]): GeocodedResult[] {
  const deduplicated: GeocodedResult[] = [];

  for (const result of results) {
    const isDuplicate = deduplicated.some(existing => {
      const distance = calculateDistance(existing.lat, existing.lng, result.lat, result.lng);
      return distance < DUPLICATE_THRESHOLD_MILES;
    });

    if (!isDuplicate) {
      deduplicated.push(result);
    }
  }

  return deduplicated;
}

// =============================================================================
// Mapbox Response to GeocodedResult Mapping
// =============================================================================

function mapMapboxFeatureToResult(feature: MapboxFeature): GeocodedResult {
  const { properties, geometry } = feature;
  const context = properties.context || {};

  const featureType = mapFeatureType(properties.feature_type);

  // Extract address components from context (only include defined values)
  const addressComponents: GeocodedResult['addressComponents'] = {
    ...(context.address?.address_number && { houseNumber: context.address.address_number }),
    ...(context.street?.name && { road: context.street.name }),
    ...(context.place?.name && { city: context.place.name }),
    ...((context.region?.name || context.region?.region_code) && {
      state: context.region?.name || context.region?.region_code
    }),
    ...(context.postcode?.name && { postcode: context.postcode.name })
  };

  // Build display name - prefer full_address, fallback to place_formatted
  const displayName = properties.full_address || properties.place_formatted || properties.name;

  // Business name for POIs
  const businessName = properties.feature_type === 'poi' ? properties.name : undefined;

  // Calculate importance score (Mapbox doesn't provide this directly)
  // Higher for more specific results, lower for broader areas
  const importanceByType: Record<AddressType, number> = {
    street_address: 1.0,
    street: 0.8,
    city: 0.5,
    region: 0.3,
    other: 0.2
  };

  return {
    lat: geometry.coordinates[1],
    lng: geometry.coordinates[0],
    displayName,
    type: featureType,
    importance: importanceByType[featureType] || 0.5,
    addressComponents,
    ...(businessName && { businessName }),
    ...(ADDRESS_WARNINGS[featureType] && { warning: ADDRESS_WARNINGS[featureType] })
  };
}

// =============================================================================
// Main Geocoding Function
// =============================================================================

/** Build Mapbox Geocoding API URL */
function buildMapboxUrl(query: string, accessToken: string, options: MapboxGeocodeOptions): string {
  const { limit = 5, types, proximity } = options;
  const baseUrl = 'https://api.mapbox.com/search/geocode/v6/forward';
  const params = new URLSearchParams({
    q: query.trim(),
    access_token: accessToken,
    limit: String(Math.min(limit * 2, 10)),
    country: 'US',
    language: 'en'
  });

  if (types && types.length > 0) {
    params.set('types', types.join(','));
  }
  if (proximity) {
    params.set('proximity', `${proximity.lng},${proximity.lat}`);
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Geocode an address or location name using Mapbox Geocoding v6 API.
 *
 * @param query - Search query (address, place name, or POI)
 * @param options - Optional configuration
 * @returns Array of geocoded results compatible with existing GeocodedResult interface
 */
export async function geocodeWithMapbox(
  query: string,
  options: MapboxGeocodeOptions = {}
): Promise<GeocodedResult[]> {
  const accessToken = env.routing.mapboxAccessToken;
  if (!accessToken) {
    console.error('[MapboxGeocoding] No access token configured');
    return [];
  }

  const url = buildMapboxUrl(query, accessToken, options);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[MapboxGeocoding] API error:', response.status, response.statusText);
      return [];
    }

    const data: MapboxGeocodeResponse = await response.json();
    if (!data.features || data.features.length === 0) {
      return [];
    }

    const results = data.features.map(mapMapboxFeatureToResult);
    return deduplicateResults(results).slice(0, options.limit || 5);
  } catch (error) {
    console.error('[MapboxGeocoding] Fetch error:', error);
    return [];
  }
}

/**
 * Geocode and return the first result (convenience function).
 */
export async function geocodeAddressSingle(query: string): Promise<GeocodedResult | null> {
  const results = await geocodeWithMapbox(query, { limit: 1 });
  return results[0] || null;
}

// =============================================================================
// Mapbox Search Box API (for POI/Business Search)
// =============================================================================

/**
 * Mapbox Search Box API supports POI/business search, unlike Geocoding v6.
 * Use this for location NAME searches (business names like "AutoZone", "Five Star Auto").
 */

interface SearchBoxOptions {
  limit?: number;
  proximity?: { lat: number; lng: number };
}

interface SearchBoxContext {
  place?: { name: string };
  region?: { name: string; region_code?: string };
  postcode?: { name: string };
  street?: { name: string };
  address?: { name: string; address_number?: string };
  country?: { name: string };
}

interface SearchBoxFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  properties: {
    mapbox_id: string;
    name: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    feature_type: string;
    poi_category?: string[];
    context: SearchBoxContext;
  };
}

interface SearchBoxResponse {
  type: 'FeatureCollection';
  features: SearchBoxFeature[];
  attribution: string;
}

/** Build Search Box API URL for POI search */
function buildSearchBoxUrl(query: string, accessToken: string, options: SearchBoxOptions): string {
  const { limit = 5, proximity } = options;
  const baseUrl = 'https://api.mapbox.com/search/searchbox/v1/forward';
  const params = new URLSearchParams({
    q: query.trim(),
    access_token: accessToken,
    limit: String(Math.min(limit * 2, 10)),
    country: 'US',
    language: 'en',
    types: 'poi'
  });

  if (proximity) {
    params.set('proximity', `${proximity.lng},${proximity.lat}`);
  }

  return `${baseUrl}?${params.toString()}`;
}

/** Map Search Box feature to GeocodedResult (same interface as Geocoding v6) */
function mapSearchBoxFeatureToResult(feature: SearchBoxFeature): GeocodedResult {
  const { properties, geometry } = feature;
  const context = properties.context || {};

  // POI results are always treated as street_address level (most specific)
  const featureType: AddressType = 'street_address';

  // Extract address components from context
  const addressComponents: GeocodedResult['addressComponents'] = {
    ...(context.address?.address_number && { houseNumber: context.address.address_number }),
    ...(context.street?.name && { road: context.street.name }),
    ...(context.place?.name && { city: context.place.name }),
    ...((context.region?.name || context.region?.region_code) && {
      state: context.region?.name || context.region?.region_code
    }),
    ...(context.postcode?.name && { postcode: context.postcode.name })
  };

  // Build display name - prefer full_address, fallback to place_formatted
  const displayName = properties.full_address || properties.place_formatted || properties.name;

  // Business name is always the POI name
  const businessName = properties.name;

  return {
    lat: geometry.coordinates[1],
    lng: geometry.coordinates[0],
    displayName,
    businessName,
    type: featureType,
    importance: 1.0, // POI results are high importance
    addressComponents
  };
}

/**
 * Search for POI/businesses using Mapbox Search Box API.
 *
 * Use this for location NAME searches (business names like "AutoZone", "Five Star Auto").
 * Mapbox Geocoding v6 does NOT support POI search - this function fills that gap.
 *
 * @param query - Business or location name to search
 * @param options - Optional configuration (limit, proximity)
 * @returns Array of geocoded results with business names
 */
export async function searchPOIWithMapbox(
  query: string,
  options: SearchBoxOptions = {}
): Promise<GeocodedResult[]> {
  const accessToken = env.routing.mapboxAccessToken;
  if (!accessToken) {
    console.error('[MapboxSearchBox] No access token configured');
    return [];
  }

  const url = buildSearchBoxUrl(query, accessToken, options);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[MapboxSearchBox] API error:', response.status, response.statusText);
      return [];
    }

    const data: SearchBoxResponse = await response.json();
    if (!data.features || data.features.length === 0) {
      return [];
    }

    const results = data.features.map(mapSearchBoxFeatureToResult);
    return deduplicateResults(results).slice(0, options.limit || 5);
  } catch (error) {
    console.error('[MapboxSearchBox] Fetch error:', error);
    return [];
  }
}

// =============================================================================
// Reverse Geocoding (get address from coordinates)
// =============================================================================

/**
 * Reverse geocode coordinates to get complete address components including postcode.
 * Useful when POI search doesn't return full address details.
 *
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns Address components including postcode, or null if not found
 */
export async function reverseGeocodeForPostcode(
  lat: number,
  lng: number
): Promise<{ postcode?: string; city?: string; state?: string } | null> {
  const accessToken = env.routing.mapboxAccessToken;
  if (!accessToken) {
    console.error('[MapboxReverseGeocode] No access token configured');
    return null;
  }

  const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&access_token=${accessToken}&types=address,postcode&country=US&language=en`;

  try {
    // eslint-disable-next-line no-console
    console.log('[MapboxReverseGeocode] Fetching for coords:', lat, lng);
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[MapboxReverseGeocode] API error:', response.status);
      return null;
    }

    const data = await response.json();
    // eslint-disable-next-line no-console
    console.log('[MapboxReverseGeocode] Response features:', data.features?.length || 0);

    if (!data.features || data.features.length === 0) {
      return null;
    }

    // Find the feature with the most complete address info
    for (const feature of data.features) {
      const context = feature.properties?.context || {};
      // eslint-disable-next-line no-console
      console.log('[MapboxReverseGeocode] Feature context:', JSON.stringify(context));
      if (context.postcode?.name) {
        // eslint-disable-next-line no-console
        console.log('[MapboxReverseGeocode] Found postcode:', context.postcode.name);
        return {
          postcode: context.postcode.name,
          city: context.place?.name,
          state: context.region?.name || context.region?.region_code
        };
      }
    }

    // eslint-disable-next-line no-console
    console.log('[MapboxReverseGeocode] No postcode found in any feature');
    return null;
  } catch (error) {
    console.error('[MapboxReverseGeocode] Fetch error:', error);
    return null;
  }
}
